import type {
  WorkflowActionEntity,
  WorkflowActionInvocation,
  WorkflowScheduledActionParams,
} from '@/api/entity_types/workflow/WorkflowAction';
import { ref } from 'vue';

export type UseActionInvocationArgs = {
  action?: WorkflowActionEntity;
};

export type InvocationOption = 'Immediate' | 'Scheduled';
export const actionInvocationOptions: InvocationOption[] = ['Immediate', 'Scheduled'];

export const useActionInvocation = ({ action }: UseActionInvocationArgs) => {
  const getInitialSelectedInvocation = (action?: WorkflowActionEntity): InvocationOption => {
    if (action) return action.invocation === 'immediate' ? 'Immediate' : 'Scheduled';
    return actionInvocationOptions[0];
  };

  const getInitialDelayInDays = (action?: WorkflowActionEntity): string => {
    if (!action?.workflow_scheduled_action?.delay_in_minutes) return '1.0';
    return `${(action.workflow_scheduled_action.delay_in_minutes / 60 / 24).toFixed(1)}`;
  };

  const selectedInvocation = ref<InvocationOption>(getInitialSelectedInvocation(action));
  const delayInDays = ref<string>(getInitialDelayInDays(action));

  const getInvocationEnum = (): WorkflowActionInvocation => {
    return selectedInvocation.value === 'Immediate' ? 'immediate' : 'scheduled';
  };

  const getScheduledActionParams = (): WorkflowScheduledActionParams | undefined => {
    if (selectedInvocation.value === 'Immediate') return undefined;
    const scheduledAction: WorkflowScheduledActionParams = {
      delay_in_minutes: Math.round(+delayInDays.value * 24 * 60),
    };

    return scheduledAction;
  };

  const resetInvocation = (action?: WorkflowActionEntity) => {
    selectedInvocation.value = getInitialSelectedInvocation(action);
    delayInDays.value = getInitialDelayInDays(action);
  };

  const updateInvocation = (invocation: InvocationOption) => {
    selectedInvocation.value = invocation;
    if (invocation === 'Immediate') delayInDays.value = '1.0';
  };

  return {
    selectedInvocation,
    delayInDays,
    getInvocationEnum,
    getScheduledActionParams,
    resetInvocation,
    updateInvocation,
  };
};
